import { BETTING_CODE, ParlayTicketModel, TicketModel, LegOddsDto } from '@/hooks/query/betting-info/types';

type TicketGroup = {
	tickets: Array<TicketModel>;
	legOdd: LegOddsDto | null;
}

// 子賽事（corner/booking）以 ParentMatchid 分組，與 newmobile 下注時改用 parent match id 一致
const getGroupMatchId = (ticket: TicketModel): number | undefined =>
	ticket?.ParentMatchid > 0 ? ticket.ParentMatchid : ticket?.Matchid;

const isSameGroup = (a: TicketModel, b: TicketModel): boolean => {
	const aId = getGroupMatchId(a);
	const bId = getGroupMatchId(b);
	return !!aId && !!bId && aId === bId;
};

const COMBO_PARLAY_ERROR_CODES: readonly number[] = [BETTING_CODE.ParlayComboByPool];

const getErrorMessageDetailKeys = (details: ParlayTicketModel['ErrorMessageDetails'] | undefined): string[] => {
	if (!details) return [];
	if (details instanceof Map) {
		return Array.from(details.keys());
	}
	return Object.keys(details);
};

export const hasNoValidParlayCombo = (comboData?: ParlayTicketModel[]): boolean =>
	!comboData?.length || comboData.every(combo => COMBO_PARLAY_ERROR_CODES.includes(combo.ErrorCode));

const groupTicketsByMatch = (tickets: Array<TicketModel>): Array<TicketGroup> => {
	return (tickets ?? []).reduce((groups, ticket) => {
		const existingGroup = groups.find(group => group.some(groupTicket => isSameGroup(groupTicket, ticket)));
		if (existingGroup) {
			existingGroup.push(ticket);
		} else {
			groups.push([ticket]);
		}
		return groups;
	}, new Array<Array<TicketModel>>()).map(group => ({ tickets: group, legOdd: null }))
}

const getTicketMatchIds = (ticket: TicketModel): number[] => {
	const ids = [ticket.Matchid];
	if (ticket.ParentMatchid > 0) {
		ids.push(ticket.ParentMatchid);
	}
	return ids;
};

const ticketMatchesLegOdd = (ticket: TicketModel, legOdd: LegOddsDto): boolean => {
	const ticketMatchIds = getTicketMatchIds(ticket);

	if (Array.isArray(legOdd.MatchIdList) && legOdd.MatchIdList.length > 0) {
		return ticketMatchIds.some(id => legOdd.MatchIdList!.includes(id));
	}

	return ticketMatchIds.includes(legOdd.MatchId);
};

const groupTicketsByLegOdds = (tickets: Array<TicketModel>, legOdds: Array<LegOddsDto>): Array<TicketGroup> => {
	return legOdds.map(legOdd => {
		const grouped = tickets.filter(ticket => ticketMatchesLegOdd(ticket, legOdd));
		return { legOdd, tickets: grouped };
	}).filter(g => g.tickets.length > 0);
}

export const bbPlusService = {
	groupTickets(tickets: Array<TicketModel>, legOdds?: Array<LegOddsDto>): Array<TicketGroup> {
		const legOddGroups = legOdds?.length ? groupTicketsByLegOdds(tickets, legOdds) : [];
		// 沒有對應 legOdd 的票 fallback 以 Matchid 分組（與 newmobile 一致）
		const groupedTickets = new Set(legOddGroups.flatMap(group => group.tickets));
		const unmatchedTickets = (tickets ?? []).filter(ticket => !groupedTickets.has(ticket));
		return [...legOddGroups, ...groupTicketsByMatch(unmatchedTickets)];
	},

	getUniqueMatchCount(tickets: Array<TicketModel> | undefined | null): number {
		return new Set((tickets ?? []).map((t) => t.ParentMatchid > 0 ? t.ParentMatchid : t.Matchid)).size;
	},
	// 傳入do combo ticket 回傳的 comboData
	// "comboData": [
	// 	{
	// 		"ErrorCode": 61,
	// 		"ErrorMsg": "Please add more matches, or remove the over-limit Combo Set(s) to proceed.",
	// 		"ErrorMessageDetails": {
	// 			"1": "",
	// 			"2": ""
	// 		},
	// 		"Name": "Bet Builder (1 Bet)"
	//      ...
	// 	}, 
	// ErrorMessageDetails key 為 ticket index 代表超過 exceed limit selection 的 ticket index
	hasParlayPoolLimitByMatchId(
		comboData: ParlayTicketModel[],
		allTickets: TicketModel[],
		matchId: number | string,
	): boolean {
		if (!Array.isArray(comboData) || comboData.length === 0) {
			return false;
		}

		return comboData.some((combo) => {
			if (combo.ErrorCode !== BETTING_CODE.ParlayComboByPool) {
				return false;
			}

			return getErrorMessageDetailKeys(combo.ErrorMessageDetails).some((idx) => {
				const affectedTicket = allTickets[parseInt(idx, 10) - 1];
				return affectedTicket && String(affectedTicket.Matchid) === String(matchId);
			});
		});
	},
};
